/* eslint-disable */

// Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import {
  AudioVideoFacade,
  ConsoleLogger,
  DefaultDeviceController,
  DefaultDOMWebSocketFactory,
  DefaultMeetingSession,
  DefaultModality,
  DefaultPromisedWebSocketFactory,
  DeviceChangeObserver,
  FullJitterBackoff,
  LogLevel,
  MeetingSession,
  MeetingSessionConfiguration,
  ReconnectingPromisedWebSocket
} from 'amazon-chime-sdk-js';
import { useIntl } from 'react-intl';

import DeviceType from '../types/DeviceType';
import FullDeviceInfoType from '../types/FullDeviceInfoType';
import MessageType from '../types/MessageType';
import RegionType from '../types/RegionType';
import RosterType from '../types/RosterType';
import commonob from '../constants/common.json'
import PubNub from "pubnub";
import { createCipherKey,globalApiService } from '../common_function'

var request = require('request');

export default class ChimeSdkWrapper implements DeviceChangeObserver {
  intl = useIntl();

  pubnub: PubNub;

  private static WEB_SOCKET_TIMEOUT_MS = 10000;

  meetingSession: MeetingSession | null = null;

  audioVideo: AudioVideoFacade | null = null;

  deviceController: DefaultDeviceController | null = null;

  title: string | null = null;

  host: boolean | null = null;

  name: string | null = null;

  region: string | null = null;

  publicChannel: string = `${commonob.app_name}-chat`;
  
  publishKey: string = commonob.pubnub.publishKey;
  
  subscribeKey: string = commonob.pubnub.subscribeKey;  

  channelArrPvt: any = [];

  totalAttendeeList: any = [];

  meetingRecorderName: string = "Unknown";

  supportedChimeRegions: RegionType[] = [
    { label: 'United States (N. Virginia)', value: 'us-east-1' },
    { label: 'Japan (Tokyo)', value: 'ap-northeast-1' },
    { label: 'Singapore', value: 'ap-southeast-1' },
    { label: 'Australia (Sydney)', value: 'ap-southeast-2' },
    { label: 'Canada', value: 'ca-central-1' },
    { label: 'Germany (Frankfurt)', value: 'eu-central-1' },
    { label: 'Sweden (Stockholm)', value: 'eu-north-1' },
    { label: 'Ireland', value: 'eu-west-1' },
    { label: 'United Kingdom (London)', value: 'eu-west-2' },
    { label: 'France (Paris)', value: 'eu-west-3' },
    { label: 'Brazil (São Paulo)', value: 'sa-east-1' },
    { label: 'United States (Ohio)', value: 'us-east-2' },
    { label: 'United States (N. California)', value: 'us-west-1' },
    { label: 'United States (Oregon)', value: 'us-west-2' }
  ];

  currentAudioInputDevice: DeviceType | null = null;

  currentAudioOutputDevice: DeviceType | null = null;

  currentVideoInputDevice: DeviceType | null = null;

  audioInputDevices: DeviceType[] = [];

  audioOutputDevices: DeviceType[] = [];

  videoInputDevices: DeviceType[] = [];

  devicesUpdatedCallbacks: ((
    fullDeviceInfo: FullDeviceInfoType
  ) => void)[] = [];

  roster: RosterType = {};

  rosterUpdateCallbacks: ((roster: RosterType) => void)[] = [];

  configuration: MeetingSessionConfiguration | null = null;

  messagingSocket: ReconnectingPromisedWebSocket | null = null;

  messageUpdateCallbacks: ((message: MessageType) => void)[] = [];

  initializeSdkWrapper = async () => {
    this.meetingSession = null;
    this.audioVideo = null;
    this.title = null;
    this.name = null;
    this.region = null;
    this.currentAudioInputDevice = null;
    this.currentAudioOutputDevice = null;
    this.currentVideoInputDevice = null;
    this.audioInputDevices = [];
    this.audioOutputDevices = [];
    this.videoInputDevices = [];
    this.roster = {};
    this.rosterUpdateCallbacks = [];
    this.configuration = null;
    this.messagingSocket = null;
    this.messageUpdateCallbacks = [];
  };

  /*
   * ====================================================================
   * regions
   * ====================================================================
   */
  lookupClosestChimeRegion = async (): Promise<RegionType> => {
    let region: string;
    try {
      //const response = await fetch(`https://l.chime.aws`, {
      const response = await fetch(`https://nearest-media-region.l.chime.aws`, {
        method: 'GET'
      });
      const json = await response.json();
      if (json.error) {
        throw new Error(
          `${this.intl.formatMessage({
            id: 'CreateOrJoin.serverError'
          })}: ${json.error}`
        );
      }
      region = json.region;
    } catch (error) {
      this.logError(error);
    }
    return (
      this.supportedChimeRegions.find(({ value }) => value === region) ||
      this.supportedChimeRegions[0]
    );
  };

  createRoom = async (
    title: string | null,
    name: string | null,
    region: string | null,
    role: string | null
  ): Promise<void> => {
    if (!title || !name || !region || !role) {
      this.logError(
        new Error(
          `title=${title} name=${name} region=${region} role=${role} must exist`
        )
      );
      return;
    }

    const response = await fetch(
      `${commonob.getBaseUrl}join?title=${encodeURIComponent(
        title
      )}&name=${encodeURIComponent(name)}&region=${encodeURIComponent(
        region
      )}&role=${encodeURIComponent(role)}`,
      {
        method: 'POST'
      }
    );
    const json = await response.json();
    if (json.error) {
      throw new Error(
        `${this.intl.formatMessage({
          id: 'CreateOrJoin.serverError'
        })}: ${json.error}`
      );
    }

    const { JoinInfo } = json;
    if (!JoinInfo) {
      throw new Error(
        this.intl.formatMessage({
          id: 'CreateOrJoin.classRoomDoesNotExist'
        })
      );
    }
    this.configuration = new MeetingSessionConfiguration(
      JoinInfo.Meeting,
      JoinInfo.Attendee
    );
    await this.initializeMeetingSession(this.configuration);

    this.title = title;
    this.name = name;
    this.region = region;
    this.host = JoinInfo.Attendee.host;
    if(JoinInfo.Attendee.host == true){
      localStorage.setItem('hostId', JoinInfo.Attendee.AttendeeId);
    }
    localStorage.setItem('currenteMeetingID', JoinInfo.Meeting.MeetingId);

    localStorage.setItem('currentAtendeeId', JoinInfo.Attendee.AttendeeId);

    this.subscribePubNub();
  
  };

  initializeDeviceController = async (): Promise<DefaultDeviceController> => {
    const logger = new ConsoleLogger('SDK', LogLevel.OFF);
    this.deviceController = new DefaultDeviceController(logger);
    return this.deviceController;
  };

  updateDeviceLists = (
    audioInputs: MediaDeviceInfo[],
    audioOutputs: MediaDeviceInfo[],
    videoInputs: MediaDeviceInfo[]
  ): void => {
    this.audioInputDevices = audioInputs.map(
      (mediaDeviceInfo: MediaDeviceInfo) => ({
        label: mediaDeviceInfo.label,
        value: mediaDeviceInfo.deviceId,
      })
    );

    this.audioOutputDevices = audioOutputs.map(
      (mediaDeviceInfo: MediaDeviceInfo) => ({
        label: mediaDeviceInfo.label,
        value: mediaDeviceInfo.deviceId,
      })
    );

    this.videoInputDevices = videoInputs.map(
      (mediaDeviceInfo: MediaDeviceInfo) => ({
        label: mediaDeviceInfo.label,
        value: mediaDeviceInfo.deviceId,
      })
    );
  };

  initializeMeetingSession = async (
    configuration: MeetingSessionConfiguration
  ): Promise<void> => {
    const logger = new ConsoleLogger('SDK', LogLevel.OFF);
    this.deviceController = new DefaultDeviceController(logger);
    this.meetingSession = new DefaultMeetingSession(
      configuration,
      logger,
      this.deviceController
    );
    this.audioVideo = this.meetingSession.audioVideo;

    this.audioInputDevices = [];
    (await this.audioVideo?.listAudioInputDevices()).forEach(
      (mediaDeviceInfo: any) => {
        this.audioInputDevices.push({
          label: mediaDeviceInfo.label,
          value: mediaDeviceInfo.deviceId
        });
      }
    );
    this.audioOutputDevices = [];
    (await this.audioVideo?.listAudioOutputDevices()).forEach(
      (mediaDeviceInfo: any) => {
        this.audioOutputDevices.push({
          label: mediaDeviceInfo.label,
          value: mediaDeviceInfo.deviceId
        });
      }
    );
    this.videoInputDevices = [];
    (await this.audioVideo?.listVideoInputDevices()).forEach(
      (mediaDeviceInfo: any) => {
        this.videoInputDevices.push({
          label: mediaDeviceInfo.label,
          value: mediaDeviceInfo.deviceId
        });
      }
    );
    this.publishDevicesUpdated();
    this.audioVideo?.addDeviceChangeObserver(this);

    this.audioVideo?.realtimeSubscribeToAttendeeIdPresence(
      (presentAttendeeId: string, present: boolean): void => {
        if (!present) {
          delete this.roster[presentAttendeeId];
          this.publishRosterUpdate();
          
          try{
              var audio = new Audio(require('../sound/ExitAttendee.wav'));
              audio.play();
          }catch(error) {
            console.log("error==",error);
          }
          return;
        }

        

        this.audioVideo?.realtimeSubscribeToVolumeIndicator(
          presentAttendeeId,
          async (
            attendeeId: string,
            volume: number | null,
            muted: boolean | null,
            signalStrength: number | null
          ) => {
            const baseAttendeeId = new DefaultModality(attendeeId).base();
            if (baseAttendeeId !== attendeeId) {
              if (
                baseAttendeeId !==
                this.meetingSession?.configuration.credentials?.attendeeId
              ) {
                // TODO: stop my content share
              }
              return;
            }

            if (!this.roster[attendeeId]) {
              this.roster[attendeeId] = { name: '' };
              var audio = new Audio('../sound/EntryAttendee.wav'); 	
              audio.play();

              this.roster[attendeeId]['history'] = false;
              this.roster[attendeeId]['msgcount'] = 0;
              this.roster[attendeeId]['isMicLoading'] = false;	
              this.roster[attendeeId]['isVodLoading'] = false;            
              this.roster[attendeeId]['isTyping'] = false;
              this.roster[attendeeId]['isTypingPvt'] = false;

            }
            if (volume !== null) {
              this.roster[attendeeId].volume = Math.round(volume * 100);
            }
            if (muted !== null) {
              this.roster[attendeeId].muted = muted;
            }
            if (signalStrength !== null) {
              this.roster[attendeeId].signalStrength = Math.round(
                signalStrength * 100
              );
            }
            
            
            if (this.title && attendeeId && !this.roster[attendeeId].name) {
            
            const response = await fetch(
                `${commonob.getBaseUrl}attendee?title=${encodeURIComponent(
                  this.title
                )}&attendee=${encodeURIComponent(attendeeId)}`
              );
              const json = await response.json();              

              if(json.AttendeeInfo.Name === this.meetingRecorderName && attendeeId !== this.meetingSession?.configuration.credentials?.attendeeId){                  
                delete this.roster[presentAttendeeId];
                this.publishRosterUpdate();
                return;
              }

              this.roster[attendeeId].name = json.AttendeeInfo?.Name || '';

              if(this.roster[this.meetingSession?.configuration.credentials?.attendeeId]?.name == this.meetingRecorderName){
                  this.audioVideo?.realtimeMuteLocalAudio();
              }

              if(this.meetingSession?.configuration.credentials?.attendeeId !== attendeeId && !this.channelArrPvt.includes(attendeeId) ){
                this.channelArrPvt.push(attendeeId);
                this.subscribePubNubPvt(attendeeId);
              }

              function attendeeExists(id:any,totalAttendeeList:any) {
                return totalAttendeeList.some(function(el:any) {
                  return el.AttendeeId === id;
                }); 
              }
                if (!attendeeExists(json.AttendeeInfo.AttendeeId,this.totalAttendeeList)) {
                  this.totalAttendeeList.push(json.AttendeeInfo)
                }

            }
            this.publishRosterUpdate();
          }
        );
      }
    );
  };


  subscribePubNub = () => {

    const cipherKey = createCipherKey(this.title);

    this.pubnub  =  new PubNub({
        publishKey: this.publishKey,
        subscribeKey: this.subscribeKey,
        uuid: this.meetingSession?.configuration.credentials?.attendeeId, // Use the UUID for identification on PubNub. 
        ssl: true,
        cipherKey: cipherKey
    })

    this.pubnub.subscribe(
        {
          channels: [`${this.publicChannel}-${this.title}`], //Only one channel, split in different rows if required and load in props, can be set by load balancer.
          withPresence: true, 
        }
      )
  }

    setHistoryTrue = (attendeeId: string) => { 
      this.roster[attendeeId]['history'] = true;
  }
  updateIstyping = (attendeeId, isTyping) => {

      if(this.roster?.[attendeeId]?.isTyping !== undefined) {
        this.roster[attendeeId]['isTyping'] = isTyping;
        this.publishRosterUpdate();
      }

  }

  updateIstypingPvt = (attendeeId, isTyping) => {

      if(this.roster?.[attendeeId]?.isTypingPvt !== undefined) {
        this.roster[attendeeId]['isTypingPvt'] = isTyping;
        this.publishRosterUpdate();
      }

  }

     updateChatMessageCounter = (attendeeId: string, count: number) => { 
      this.roster[attendeeId]['msgcount'] = count;
  }
  setChannelName = (attendeeId: string) => {

      let chnlArr = [];
      chnlArr[0] = this.meetingSession?.configuration.credentials?.attendeeId;
      chnlArr[1] = attendeeId;
      let channel = chnlArr.sort().join("-")
      channel = (this.title + "-" + channel); 
      return channel;
  }

  subscribePubNubPvt = async (attendeeId: string) => {

      const channel = await this.setChannelName(attendeeId)
      
      this.pubnub.subscribe(
        {
          channels: [channel], //Only one channel, split in different rows if required and load in props, can be set by load balancer.
          withPresence: true
        }
      )
  }

  listAndSelectDevices = async (): Promise<void> => {
    const audioInputs =
      (await this.deviceController?.listAudioInputDevices()) || [];
    const audioOutputs =
      (await this.deviceController?.listAudioOutputDevices()) || [];
    const videoInputs =
      (await this.deviceController?.listVideoInputDevices()) || [];

    this.updateDeviceLists(audioInputs, audioOutputs, videoInputs);

    if (
      !this.currentAudioInputDevice &&
      audioInputs.length &&
      audioInputs[0].deviceId
    ) {
      this.currentAudioInputDevice = {
        label: audioInputs[0].label,
        value: audioInputs[0].deviceId,
      };
      await this.deviceController?.chooseAudioInputDevice(
        audioInputs[0].deviceId
      );
    }

    if (
      !this.currentAudioOutputDevice &&
      audioOutputs.length &&
      audioOutputs[0].deviceId 
      // &&
      // this.browserBehavior.supportsSetSinkId()
    ) {
      this.currentAudioOutputDevice = {
        label: audioOutputs[0].label,
        value: audioOutputs[0].deviceId,
      };
      await this.deviceController?.chooseAudioOutputDevice(
        audioOutputs[0].deviceId
      );
    }

    if (
      !this.currentVideoInputDevice &&
      videoInputs.length &&
      videoInputs[0].deviceId
    ) {
      this.currentVideoInputDevice = {
        label: videoInputs[0].label,
        value: videoInputs[0].deviceId,
      };

      await this.deviceController?.chooseVideoInputDevice(
        videoInputs[0].deviceId
      );
    }

    this.publishDevicesUpdated();
  };

  joinRoom = async (element: HTMLAudioElement | null): Promise<void> => {
    if (!element) {
      this.logError(new Error(`element does not exist`));
      return;
    }

    window.addEventListener(
      'unhandledrejection',
      (event: PromiseRejectionEvent) => {
        this.logError(event.reason);
      }
    );

    const audioInputs = await this.audioVideo?.listAudioInputDevices();
    if (audioInputs && audioInputs.length > 0 && audioInputs[0].deviceId) {
      this.currentAudioInputDevice = {
        label: audioInputs[0].label,
        value: audioInputs[0].deviceId
      };
      await this.audioVideo?.chooseAudioInputDevice(audioInputs[0].deviceId);
    }

    const audioOutputs = await this.audioVideo?.listAudioOutputDevices();
    if (audioOutputs && audioOutputs.length > 0 && audioOutputs[0].deviceId) {
      this.currentAudioOutputDevice = {
        label: audioOutputs[0].label,
        value: audioOutputs[0].deviceId
      };
      await this.audioVideo?.chooseAudioOutputDevice(audioOutputs[0].deviceId);
    }

    const videoInputs = await this.audioVideo?.listVideoInputDevices();
    if (videoInputs && videoInputs.length > 0 && videoInputs[0].deviceId) {
      this.currentVideoInputDevice = {
        label: videoInputs[0].label,
        value: videoInputs[0].deviceId
      };
      await this.audioVideo?.chooseVideoInputDevice(null);
    }

    this.publishDevicesUpdated();
    this.roster = {}

    this.audioVideo?.bindAudioElement(element);
    this.audioVideo?.start();
  };

  joinRoomMessaging = async (): Promise<void> => {
    if (!this.configuration) {
      this.logError(new Error('configuration does not exist'));
      return;
    }

    const messagingUrl = `${commonob.getMessagingWssUrl}?MeetingId=${
      this.configuration.meetingId
    }&AttendeeId=${this.configuration.credentials?.attendeeId}&JoinToken=${
      this.configuration.credentials?.joinToken
    }`;
    this.messagingSocket = new ReconnectingPromisedWebSocket(
      messagingUrl,
      [],
      'arraybuffer',
      new DefaultPromisedWebSocketFactory(new DefaultDOMWebSocketFactory()),
      new FullJitterBackoff(1000, 0, 10000)
    );

    await this.messagingSocket.open(ChimeSdkWrapper.WEB_SOCKET_TIMEOUT_MS);

    this.messagingSocket.addEventListener('message', (event: Event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        const { attendeeId } = data.payload;

        let name;
        if (this.roster[attendeeId]) {
          name = this.roster[attendeeId].name;
        }

        this.publishMessageUpdate({
          type: data.type,
          payload: data.payload,
          timestampMs: Date.now(),
          name
        });
      } catch (error) {
        this.logError(error);
      }
    });
  };

  // eslint-disable-next-line
  sendMessage = (type: string, payload: any) => {
    if (!this.messagingSocket) {
      return;
    }
    const message = {
      message: 'sendmessage',
      data: JSON.stringify({ type, payload })
    };
    try {
      this.messagingSocket.send(JSON.stringify(message));
    } catch (error) {
      this.logError(error);
    }
  };

  leaveRoom = async (end: boolean,recordingUrls:any,timeDuration:any): Promise<void> => {
    try {
      this.audioVideo.chooseVideoInputDevice(null);
      this.audioVideo.stopLocalVideoTile();
      this.audioVideo?.stop();
    } catch (error) {
      this.logError(error);
    }

    try {
      await this.messagingSocket?.close(ChimeSdkWrapper.WEB_SOCKET_TIMEOUT_MS);
    } catch (error) {
      this.logError(error);
    }

    try {
      if (end && this.title) {
        await fetch(
          `${commonob.getBaseUrl}end?title=${encodeURIComponent(this.title)}`,
          {
            method: 'POST'
          }
        );
      }
    } catch (error) {
      this.logError(error);
    }

    const clearLocalStorage = () => {
      localStorage.removeItem("currentAtendeeMeetingTitle")
      localStorage.removeItem("isRightBarEnabledSettings")
      localStorage.removeItem("shareScreen")
      localStorage.removeItem("currenteMeetingID")
      localStorage.removeItem("hostId")
      localStorage.removeItem("created_by")
      localStorage.removeItem("currentAtendeeId");
    }

    const submitMeetingReport = async() => {
      var raw = JSON.stringify(
        {
          "email_id": `${localStorage.getItem("userName")}`,
          "attendee_name": `${localStorage.getItem("currentAtendeeName")}`,
          "meeting_id": `${localStorage.getItem("currentAtendeeMeetingTitle")}`,
          "recording_url": recordingUrls,
          "time_duration": timeDuration,
          "total_attendee_list": this.totalAttendeeList,
          "actual_meeting_id": `${localStorage.getItem("currenteMeetingID")}`,
          "attendee_id": `${localStorage.getItem("currentAtendeeId")}`
        });


        var requestOption = {
          "method":"POST",
          "body":raw,
          "url":`${commonob.getBaseUrl}submitAttendeeMeetingReport`
        }

        try{
          let result:any = await globalApiService(requestOption);
        } catch (e){
          console.log(e)
        }
    }

    const sendRecordingUrl = async() => {
      var myHeaders = new Headers();
      myHeaders.append("Authorization", commonob.recorded_url_auth);
      var raw = JSON.stringify(
        {
          "email": `${localStorage.getItem("userName")}`,
          "name": `${localStorage.getItem("currentAtendeeName")}`,
          "meeting_id": `${localStorage.getItem("currentAtendeeMeetingTitle")}`,
          "recording_url": recordingUrls
        });

        var requestOption = {
          "method":"POST",
          "body":raw,
          "headers":myHeaders,
          "url":`${commonob.getBaseUrl}recordings`
        }
        try{
          let result:any = await globalApiService(requestOption);
        } catch (e){
          console.log(e)
        }
      }
    try {
      if (recordingUrls.length > 0) {
        sendRecordingUrl()
      }
      submitMeetingReport()
    } catch (error) {
      this.logError(error);
    }
    clearLocalStorage()
    this.initializeSdkWrapper();
  };

  /**
   * ====================================================================
   * Device
   * ====================================================================
   */

  chooseAudioInputDevice = async (device: DeviceType) => {
    try {
      await this.audioVideo?.chooseAudioInputDevice(device.value);
      this.currentAudioInputDevice = device;
    } catch (error) {
      this.logError(error);
    }
  };

  chooseAudioOutputDevice = async (device: DeviceType) => {
    try {
      await this.audioVideo?.chooseAudioOutputDevice(device.value);
      this.currentAudioOutputDevice = device;
    } catch (error) {
      this.logError(error);
    }
  };

  chooseVideoInputDevice = async (device: DeviceType) => {
    try {
      await this.audioVideo?.chooseVideoInputDevice(device.value);
      this.currentVideoInputDevice = device;
    } catch (error) {
      this.logError(error);
    }
  };

  /**
   * ====================================================================
   * Observer methods
   * ====================================================================
   */

  audioInputsChanged(freshAudioInputDeviceList: any): void {
    let hasCurrentDevice = false;
    this.audioInputDevices = [];
    freshAudioInputDeviceList.forEach((mediaDeviceInfo: any) => {
      if (
        this.currentAudioInputDevice &&
        mediaDeviceInfo.deviceId === this.currentAudioInputDevice.value
      ) {
        hasCurrentDevice = true;
      }
      this.audioInputDevices.push({
        label: mediaDeviceInfo.label,
        value: mediaDeviceInfo.deviceId
      });
    });
    if (!hasCurrentDevice) {
      this.currentAudioInputDevice =
        this.audioInputDevices.length > 0 ? this.audioInputDevices[0] : null;
    }
    this.publishDevicesUpdated();
  }

  audioOutputsChanged(freshAudioOutputDeviceList: any): void {
    let hasCurrentDevice = false;
    this.audioOutputDevices = [];
    freshAudioOutputDeviceList.forEach((mediaDeviceInfo: any) => {
      if (
        this.currentAudioOutputDevice &&
        mediaDeviceInfo.deviceId === this.currentAudioOutputDevice.value
      ) {
        hasCurrentDevice = true;
      }
      this.audioOutputDevices.push({
        label: mediaDeviceInfo.label,
        value: mediaDeviceInfo.deviceId
      });
    });
    if (!hasCurrentDevice) {
      this.currentAudioOutputDevice =
        this.audioOutputDevices.length > 0 ? this.audioOutputDevices[0] : null;
    }
    this.publishDevicesUpdated();
  }

  videoInputsChanged(freshVideoInputDeviceList: any): void {
    let hasCurrentDevice = false;
    this.videoInputDevices = [];
    freshVideoInputDeviceList.forEach((mediaDeviceInfo: any) => {
      if (
        this.currentVideoInputDevice &&
        mediaDeviceInfo.deviceId === this.currentVideoInputDevice.value
      ) {
        hasCurrentDevice = true;
      }
      this.videoInputDevices.push({
        label: mediaDeviceInfo.label,
        value: mediaDeviceInfo.deviceId
      });
    });
    if (!hasCurrentDevice) {
      this.currentVideoInputDevice =
        this.videoInputDevices.length > 0 ? this.videoInputDevices[0] : null;
    }
    this.publishDevicesUpdated();
  }

  /**
   * ====================================================================
   * Subscribe and unsubscribe from SDK events
   * ====================================================================
   */

  subscribeToDevicesUpdated = (
    callback: (fullDeviceInfo: FullDeviceInfoType) => void
  ) => {
    this.devicesUpdatedCallbacks.push(callback);
  };

  unsubscribeFromDevicesUpdated = (
    callback: (fullDeviceInfo: FullDeviceInfoType) => void
  ) => {
    const index = this.devicesUpdatedCallbacks.indexOf(callback);
    if (index !== -1) {
      this.devicesUpdatedCallbacks.splice(index, 1);
    }
  };

  private publishDevicesUpdated = () => {
    this.devicesUpdatedCallbacks.forEach(
      (callback: (fullDeviceInfo: FullDeviceInfoType) => void) => {
        callback({
          currentAudioInputDevice: this.currentAudioInputDevice,
          currentAudioOutputDevice: this.currentAudioOutputDevice,
          currentVideoInputDevice: this.currentVideoInputDevice,
          audioInputDevices: this.audioInputDevices,
          audioOutputDevices: this.audioOutputDevices,
          videoInputDevices: this.videoInputDevices
        });
      }
    );
  };

  subscribeToRosterUpdate = (callback: (roster: RosterType) => void) => {
    this.rosterUpdateCallbacks.push(callback);
  };

  unsubscribeFromRosterUpdate = (callback: (roster: RosterType) => void) => {
    const index = this.rosterUpdateCallbacks.indexOf(callback);
    if (index !== -1) {
      this.rosterUpdateCallbacks.splice(index, 1);
    }
  };

  private publishRosterUpdate = () => {
    for (let i = 0; i < this.rosterUpdateCallbacks.length; i += 1) {
      const callback = this.rosterUpdateCallbacks[i];
      callback(this.roster);
    }
  };

  subscribeToMessageUpdate = (callback: (message: MessageType) => void) => {
    this.messageUpdateCallbacks.push(callback);
  };

  unsubscribeFromMessageUpdate = (callback: (message: MessageType) => void) => {
    const index = this.messageUpdateCallbacks.indexOf(callback);
    if (index !== -1) {
      this.messageUpdateCallbacks.splice(index, 1);
    }
  };

  private publishMessageUpdate = (message: MessageType) => {
    for (let i = 0; i < this.messageUpdateCallbacks.length; i += 1) {
      const callback = this.messageUpdateCallbacks[i];
      callback(message);
    }
  };

  /**
   * ====================================================================
   * Utilities
   * ====================================================================
   */
  private logError = (error: Error) => {
    // eslint-disable-next-line
    console.error(error);
  };
}
